import { useState, type ReactNode } from 'react';
import {
  Badge,
  Box,
  Button,
  CodeBlock,
  Icon,
  Link,
  ListItem,
  Text,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import {
  AttributeTypeLabel,
  CREATED_IN_CIO_AT,
  IdentifierKey,
  isAttributeEnabledIdentifier,
  isValidTimestamp,
  type IdentifiersConfig,
} from '@/features/people';
import { formatLongTimestamp } from '@/utils/calendar-timestamps';
import { isEmptyString } from '@/utils/string';
import type { ProfileAttribute } from '../utils/profile-attributes';
import { isJsonAttribute, isGeoIpAttribute } from '../utils/attribute-values';
import * as styles from '../styles.css';

type ProfileAttributeRowProps = {
  attribute: ProfileAttribute;
  identifiers: IdentifiersConfig | null | undefined;
  isMultiIdentifier: boolean;
  isEmailIdentified: boolean;
  /** The workspace's plural for people, for the identifier explanation. */
  peoplePlural: string;
  /** Where identifiers are managed. */
  identifiersSettingsHref: string;
  /** The value is shown struck through: it is lost in the merge. */
  isLineThrough?: boolean;
  /** The row was contributed by the secondary profile (the merged card scrolls to the first such row). */
  isFromSecondary?: boolean;
  /** The status marker beside the value. */
  icon?: ReactNode;
};

type IdentifierBadgeProps = {
  isMultiIdentifier: boolean;
  peoplePlural: string;
  identifiersSettingsHref: string;
};

/** The "Identifier" badge; its explanation is offered only where a workspace has several identifiers. */
function IdentifierBadge({ isMultiIdentifier, peoplePlural, identifiersSettingsHref }: IdentifierBadgeProps) {
  return (
    <Tooltip isDisabled={!isMultiIdentifier} isCustom>
      {(context) => (
        <>
          <Badge
            color="grey"
            cursor="help"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            data-test="attribute-type-label"
          >
            Identifier
          </Badge>
          <TooltipContent>
            An identifier is a unique value used to identify {peoplePlural} when adding, updating, or tracking a
            profile.
            <br />
            <Link href={identifiersSettingsHref} color="inherit" isUnderlined>
              Manage your identifiers
            </Link>
          </TooltipContent>
        </>
      )}
    </Tooltip>
  );
}

type AutoGeneratedTooltipProps = { attributeKey: string };

/** The help marker on a value Customer.io assigns itself. */
function AutoGeneratedTooltip({ attributeKey }: AutoGeneratedTooltipProps) {
  return (
    <Tooltip placement="top" isCustom>
      {(context) => (
        <>
          <Icon
            name="help"
            size="sm"
            color="subtle"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          />
          <TooltipContent>
            {attributeKey === IdentifierKey.CioId ? (
              <>
                We automatically generate this value as our own database key.{' '}
                <Link
                  href="https://docs.customer.io/identifying-people/#cio_id"
                  color="inherit"
                  isUnderlined
                  isExternal
                >
                  Learn more
                </Link>
              </>
            ) : (
              'Auto-generated by Customer.io'
            )}
          </TooltipContent>
        </>
      )}
    </Tooltip>
  );
}

function isAutoGenerated(key: string, isEmailIdentified: boolean): boolean {
  if (key === IdentifierKey.CioId || key === CREATED_IN_CIO_AT || isGeoIpAttribute(key)) {
    return true;
  }
  return key === IdentifierKey.Id && isEmailIdentified;
}

type AttributeValueProps = {
  value: string;
  isTimestamp: boolean;
};

/** The value column: JSON expands into a formatted block, a timestamp gets its calendar reading, a blank shows a dash. */
function AttributeValue({ value, isTimestamp }: AttributeValueProps) {
  const [isJsonExpanded, setIsJsonExpanded] = useState(false);
  const isJson = isJsonAttribute(value);

  return (
    <>
      {isJson ? (
        <Box as="span" display="block">
          <Button variant="secondary" size="sm" mb="100" onClick={() => setIsJsonExpanded((expanded) => !expanded)}>
            {isJsonExpanded ? 'Collapse JSON' : 'Expand JSON'}
          </Button>
        </Box>
      ) : null}
      <Text as="span" className={`fs-mask ${styles.attributeValue}`} data-test="person-attribute-value">
        {isJson && isJsonExpanded ? (
          <CodeBlock code={JSON.stringify(JSON.parse(value), null, 2)} language="json" />
        ) : (
          value
        )}
      </Text>
      {isTimestamp ? (
        <Text as="span" color="subtle" ml="150">
          {formatLongTimestamp(Number(value))}
        </Text>
      ) : null}
      {isEmptyString(value) ? '–' : null}
    </>
  );
}

/** One attribute of a merge card: its name with the badges that explain it, its value, and a status marker. */
export function ProfileAttributeRow({
  attribute,
  identifiers,
  isMultiIdentifier,
  isEmailIdentified,
  peoplePlural,
  identifiersSettingsHref,
  isLineThrough = false,
  isFromSecondary = false,
  icon,
}: ProfileAttributeRowProps) {
  const { key, value } = attribute;
  const isIdentifier = isAttributeEnabledIdentifier(key, identifiers);
  const isTimestamp = !isIdentifier && isValidTimestamp(value);

  return (
    <ListItem
      display="flex"
      alignItems="center"
      px="300"
      className={styles.attributeRow}
      data-test="merge-customer-attribute"
      data-attribute={key}
      data-from-secondary={isFromSecondary ? 'true' : undefined}
    >
      <Box flex={1}>
        <Box className={styles.attributeKey}>
          <Text
            as="span"
            display="flex"
            alignItems="center"
            size="xs"
            weight="regular"
            color="subtle"
            overflowWrap="words"
          >
            <Text as="span" pr="025" data-test="person-attribute-key">
              {key}
            </Text>
            {isAutoGenerated(key, isEmailIdentified) ? <AutoGeneratedTooltip attributeKey={key} /> : null}
            <Box as="span" ml="050" display="inline-flex">
              {isIdentifier ? (
                <IdentifierBadge
                  isMultiIdentifier={isMultiIdentifier}
                  peoplePlural={peoplePlural}
                  identifiersSettingsHref={identifiersSettingsHref}
                />
              ) : (
                <AttributeTypeLabel attribute={key} identifiers={identifiers} />
              )}
            </Box>
          </Text>
        </Box>
        <Box className={isLineThrough ? styles.lineThrough : undefined}>
          <AttributeValue value={value} isTimestamp={isTimestamp} />
        </Box>
      </Box>
      <Text data-test="person-attribute-icon">{icon}</Text>
    </ListItem>
  );
}
